import { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  useContractFiles,
  useUploadContractFile,
  useDeleteContractFile,
} from '../hooks';
import { contractFilesApi } from '../api';
import api from '../../api/client';
import type { Contract, ContractFile, ContractStatus } from '../types';
import styles from './CompanyDetailPage.module.css';

const STATUS_LABELS: Record<ContractStatus, string> = {
  DRAFT: 'Entwurf',
  ACTIVE: 'Aktiv',
  EXPIRED: 'Abgelaufen',
  CANCELLED: 'Storniert',
};

const STATUS_COLORS: Record<ContractStatus, { bg: string; color: string }> = {
  DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
  ACTIVE: { bg: '#dcfce7', color: '#16a34a' },
  EXPIRED: { bg: '#fef3c7', color: '#d97706' },
  CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
};

const ALLOWED_TYPES = '.pdf,.doc,.docx,.xlsx,.xls';
const MAX_FILE_SIZE = 25 * 1024 * 1024;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatValue(val: string | null, currency = 'EUR'): string {
  if (!val) return '—';
  const num = parseFloat(val);
  if (isNaN(num)) return val;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(num);
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ContractDetailPage() {
  const { companyId, contractId } = useParams<{ companyId: string; contractId: string }>();

  const { data, isLoading, error } = useQuery<{ data: Contract }>({
    queryKey: ['crm', 'contracts', companyId, contractId],
    queryFn: () =>
      api
        .get(`/crm/companies/${companyId}/contracts/${contractId}`)
        .then((r) => r.data),
    enabled: !!companyId && !!contractId,
  });

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState('');

  if (isLoading) return <p>Laden...</p>;
  if (error || !data) return <p style={{ color: 'var(--color-error)' }}>Vertrag nicht gefunden</p>;

  const contract = data.data;

  const handleViewPdf = async (file: ContractFile) => {
    try {
      const blob = await contractFilesApi.download(companyId!, contractId!, file.id);
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfFileName(file.originalName);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <Link to={`/crm/companies/${companyId}`} className={styles.backLink}>
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 2L4 7l5 5" />
        </svg>
        Zurück zum Unternehmen
      </Link>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div>
            <h1 className={styles.name}>{contract.title}</h1>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.25rem', alignItems: 'center' }}>
              <span
                style={{
                  fontSize: '0.75rem', fontWeight: 600,
                  padding: '0.125rem 0.5rem', borderRadius: '999px',
                  background: STATUS_COLORS[contract.status].bg,
                  color: STATUS_COLORS[contract.status].color,
                }}
              >
                {STATUS_LABELS[contract.status]}
              </span>
              {contract.value && (
                <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  {formatValue(contract.value, contract.currency)}
                </span>
              )}
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                {formatDate(contract.startDate)}
                {contract.endDate ? ` – ${formatDate(contract.endDate)}` : ''}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: pdfUrl ? '300px 1fr' : '1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
        {/* Left: Contract info + Files */}
        <div>
          {/* Notizen */}
          {contract.notes && (
            <div className={styles.card} style={{ marginBottom: '1rem' }}>
              <h2 className={styles.cardTitle}>Notizen</h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', margin: 0 }}>
                {contract.notes}
              </p>
            </div>
          )}

          {/* Dokumente */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Dokumente</h2>
            <ContractFilesSection
              companyId={companyId!}
              contractId={contractId!}
              onViewPdf={handleViewPdf}
              activePdfName={pdfFileName}
            />
          </div>
        </div>

        {/* Right: PDF Viewer */}
        {pdfUrl && (
          <div className={styles.card} style={{ display: 'flex', flexDirection: 'column', minHeight: '70vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>{pdfFileName}</h2>
              <button
                onClick={() => { setPdfUrl(null); setPdfFileName(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-text-muted)', fontSize: '1.25rem', padding: '0.25rem',
                }}
                title="Schließen"
              >
                ×
              </button>
            </div>
            <iframe
              src={pdfUrl}
              style={{
                flex: 1, width: '100%', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', minHeight: '60vh',
              }}
              title={pdfFileName}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Files Section for Detail Page ──

function ContractFilesSection({
  companyId,
  contractId,
  onViewPdf,
  activePdfName,
}: {
  companyId: string;
  contractId: string;
  onViewPdf: (file: ContractFile) => void;
  activePdfName: string;
}) {
  const { data, isLoading } = useContractFiles(companyId, contractId);
  const uploadMut = useUploadContractFile(companyId, contractId);
  const deleteMut = useDeleteContractFile(companyId, contractId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState('');

  const files: ContractFile[] = data?.data ?? [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`Datei zu groß (max. 25 MB)`);
      e.target.value = '';
      return;
    }
    uploadMut.mutate(file, {
      onError: () => setUploadError('Upload fehlgeschlagen'),
    });
    e.target.value = '';
  };

  const handleDownload = async (file: ContractFile) => {
    try {
      const blob = await contractFilesApi.download(companyId, contractId, file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMut.isPending}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            padding: '0.25rem 0.625rem', background: 'var(--color-bg)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
            fontSize: '0.8125rem', cursor: uploadMut.isPending ? 'wait' : 'pointer',
            color: 'var(--color-text)', opacity: uploadMut.isPending ? 0.6 : 1,
          }}
        >
          {uploadMut.isPending ? 'Hochladen...' : '+ Hochladen'}
        </button>
        <input ref={fileInputRef} type="file" accept={ALLOWED_TYPES} style={{ display: 'none' }} onChange={handleFileChange} />
      </div>

      {uploadError && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-error)', marginBottom: '0.5rem' }}>{uploadError}</p>
      )}

      {isLoading ? (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Laden...</p>
      ) : files.length === 0 ? (
        <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
          Noch keine Dokumente hochgeladen
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          {files.map((f) => {
            const isPdf = f.mimeType === 'application/pdf';
            const isActive = f.originalName === activePdfName;
            return (
              <div
                key={f.id}
                onClick={() => isPdf && onViewPdf(f)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 0.625rem',
                  background: isActive ? 'var(--color-primary-light, #eff6ff)' : 'var(--color-bg)',
                  border: isActive ? '1px solid var(--color-primary)' : '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: isPdf ? 'pointer' : 'default',
                }}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>
                  {isPdf ? '📄' : f.mimeType.includes('word') ? '📝' : '📎'}
                </span>
                <span style={{
                  flex: 1, fontSize: '0.8125rem', color: 'var(--color-text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }} title={f.originalName}>
                  {f.originalName}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                  {formatFileSize(f.size)}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDownload(f); }}
                  title="Herunterladen"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: 'var(--color-primary)', flexShrink: 0 }}
                >
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 1v7M3 8l3 3 3-3M1 11h10" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Datei "${f.originalName}" wirklich löschen?`)) deleteMut.mutate(f.id);
                  }}
                  title="Löschen"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem', color: 'var(--color-text-muted)', flexShrink: 0 }}
                >
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M3 3l6 6M9 3l-6 6" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
